import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App";

// No-op when REACT_APP_SENTRY_DSN is unset, so safe to ship without wiring.
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENV || "production",
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
  });
}

// ?clearcache=1 drops every pg_* key before React mounts, then reloads to
// the canonical URL. Shareable with testers without walking them through
// DevTools; harmless no-op when the flag isn't present.
const USER_DATA_KEYS = new Set(["pg_favorites", "pg_school_overrides", "pg_hidden"]);
try {
  if (new URLSearchParams(window.location.search).get("clearcache") === "1") {
    Object.keys(localStorage).filter(k => k.startsWith("pg_") && !USER_DATA_KEYS.has(k)).forEach(k => localStorage.removeItem(k));
    const url = new URL(window.location.href);
    url.searchParams.delete("clearcache");
    window.location.replace(url.toString());
  }
} catch {}

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(err) { return { error: err }; }
  componentDidCatch(err, info) {
    console.error("[ErrorBoundary] Caught render error:", err, info?.componentStack);
    Sentry.captureException(err, { extra: { componentStack: info?.componentStack } });
  }
  render() {
    if (this.state.error) return (
      <div style={{ background: "#0f172a", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, color: "white", fontFamily: "sans-serif" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>😕</div>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Something went wrong</div>
        <div style={{ color: "#94a3b8", fontSize: 14, marginBottom: 8, textAlign: "center" }}>Try reloading the app. If it keeps happening, use the button below to reset.</div>
        <div style={{ color: "#ef4444", fontSize: 11, fontFamily: "monospace", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6, padding: "6px 10px", marginBottom: 16, maxWidth: 320, wordBreak: "break-all", textAlign: "left" }}>{String(this.state.error)}</div>
        <button onClick={() => window.location.reload()} style={{ background: "#fbbf24", color: "#000", border: "none", borderRadius: 10, padding: "12px 24px", fontWeight: 700, fontSize: 15, cursor: "pointer", marginBottom: 12 }}>Reload App</button>
        <button onClick={() => {
          try { Object.keys(localStorage).filter(k => k.startsWith("pg_") && !USER_DATA_KEYS.has(k)).forEach(k => localStorage.removeItem(k)); } catch {}
          window.location.reload();
        }} style={{ background: "none", color: "#64748b", border: "1px solid #334155", borderRadius: 10, padding: "10px 20px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Clear cache &amp; reload</button>
      </div>
    );
    return this.props.children;
  }
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<React.StrictMode><ErrorBoundary><App /></ErrorBoundary></React.StrictMode>);
